import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  LinearProgress,
  Stack,
  Slider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VideocamIcon from '@mui/icons-material/Videocam';
import { useApi } from '../contexts/ApiContext';
import { useNotification } from '../contexts/NotificationContext';
import {
  getAgentStatusLabel,
  getAgentStatusColor,
  getAgentStatusTextColor
} from '../constants/agentStatus';

const FILTER_CONTROL_SX = {
  '& .MuiInputBase-root': { minHeight: 34, fontSize: '0.9rem' },
  '& .MuiInputLabel-root': { fontSize: '0.85rem' },
  '& .MuiSelect-select': { py: 0.4 }
};

const ONLINE_STATUS_LIST = [
  'AVAILABLE',
  'BUSY',
  'PAUSE',
  'NOTREADY',
  'ABSENT',
  'BREAK',
  'BATHROOM',
  'LUNCH',
  'REST',
  'TRAINING'
];
const ONLINE_STATUS_SET = new Set(ONLINE_STATUS_LIST);

const isOnlineStatus = (status) => ONLINE_STATUS_SET.has(status);

const isAgentRole = (agent) => {
  const rawRole =
    agent?.role?.role_name ||
    agent?.role?.name ||
    agent?.role_name ||
    agent?.role ||
    '';
  if (!rawRole) return false;
  const normalized = String(rawRole).trim().toLowerCase();
  return normalized === 'agent' || normalized === 'agente';
};

const DEFAULT_FILTERS = {
  agent: '',
  campaignId: '',
  skillId: '',
  status: '',
  supervisorId: '',
  managerId: '',
  hideDisconnected: true,
  onlyAgents: true
};
const PREFS_KEY = 'cti_monitor_center_prefs_v1';

const SCREEN_TILE_MIN = 220;
const SCREEN_TILE_MAX = 800;

const startNoise = (canvas) => {
  if (!canvas) return () => {};
  const context = canvas.getContext('2d');
  if (!context) return () => {};

  const scaleFactor = 2.5;
  const FPS = 50;
  const scanSpeed = FPS * 15;
  const SAMPLE_COUNT = 10;
  let samples = [];
  let sampleIndex = 0;
  let scanOffsetY = 0;
  let scanSize = 0;
  let rafId = null;

  const interpolate = (x, x0, y0, x1, y1) =>
    y0 + (y1 - y0) * ((x - x0) / (x1 - x0));

  const generateRandomSample = (w, h) => {
    const intensity = [];
    const factor = h / 50;
    const trans = 1 - Math.random() * 0.05;

    const intensityCurve = [];
    for (let i = 0; i < Math.floor(h / factor) + factor; i += 1) {
      intensityCurve.push(Math.floor(Math.random() * 15));
    }

    for (let i = 0; i < h; i += 1) {
      const value = interpolate(
        i / factor,
        Math.floor(i / factor),
        intensityCurve[Math.floor(i / factor)],
        Math.floor(i / factor) + 1,
        intensityCurve[Math.floor(i / factor) + 1]
      );
      intensity.push(value);
    }

    const imageData = context.createImageData(w, h);
    for (let i = 0; i < w * h; i += 1) {
      const k = i * 4;
      let color = Math.floor(36 * Math.random());
      color += intensity[Math.floor(i / w)];
      imageData.data[k] = color;
      imageData.data[k + 1] = color;
      imageData.data[k + 2] = color;
      imageData.data[k + 3] = Math.round(255 * trans);
    }
    return imageData;
  };

  const resize = () => {
    const width = Math.max(1, Math.floor(canvas.offsetWidth / scaleFactor));
    const height = Math.max(1, Math.floor(width / (canvas.offsetWidth / canvas.offsetHeight || 1)));
    canvas.width = width;
    canvas.height = height;
    scanSize = (canvas.offsetHeight / scaleFactor) / 3;
    samples = [];
    for (let i = 0; i < SAMPLE_COUNT; i += 1) {
      samples.push(generateRandomSample(canvas.width, canvas.height));
    }
  };

  const render = () => {
    context.putImageData(samples[Math.floor(sampleIndex)], 0, 0);
    sampleIndex += 20 / FPS;
    if (sampleIndex >= samples.length) sampleIndex = 0;

    const grd = context.createLinearGradient(0, scanOffsetY, 0, scanSize + scanOffsetY);
    grd.addColorStop(0, 'rgba(255,255,255,0)');
    grd.addColorStop(0.1, 'rgba(255,255,255,0)');
    grd.addColorStop(0.2, 'rgba(255,255,255,0.2)');
    grd.addColorStop(0.3, 'rgba(255,255,255,0.0)');
    grd.addColorStop(0.45, 'rgba(255,255,255,0.1)');
    grd.addColorStop(0.5, 'rgba(255,255,255,1.0)');
    grd.addColorStop(0.55, 'rgba(255,255,255,0.55)');
    grd.addColorStop(0.6, 'rgba(255,255,255,0.25)');
    grd.addColorStop(1, 'rgba(255,255,255,0)');

    context.fillStyle = grd;
    context.fillRect(0, scanOffsetY, canvas.width, scanSize + scanOffsetY);
    context.globalCompositeOperation = 'lighter';

    scanOffsetY += canvas.height / scanSpeed;
    if (scanOffsetY > canvas.height) scanOffsetY = -(scanSize / 2);

    rafId = window.requestAnimationFrame(render);
  };

  resize();
  rafId = window.requestAnimationFrame(render);

  const observer = new ResizeObserver(() => resize());
  observer.observe(canvas);

  return () => {
    if (rafId) cancelAnimationFrame(rafId);
    observer.disconnect();
  };
};

const ScreenTile = ({
  agent,
  isActive,
  onClick,
  onCameraClick,
  isFullscreen,
  onExitFullscreen
}) => {
  const tileRef = useRef(null);
  const unloadTimerRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const noiseCanvasRef = useRef(null);
  const statusLabel = getAgentStatusLabel(agent.status) || agent.status || 'Sin estado';
  const statusColor = getAgentStatusColor(agent.status);
  const statusTextColor = getAgentStatusTextColor(agent.status);
  const iframeSrc = shouldLoad ? `/viewer?id=${encodeURIComponent(agent.agent)}&quality=low&request=1` : '';

  useEffect(() => {
    if (!tileRef.current) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (unloadTimerRef.current) {
              clearTimeout(unloadTimerRef.current);
              unloadTimerRef.current = null;
            }
            setShouldLoad(true);
          } else if (!unloadTimerRef.current) {
            unloadTimerRef.current = setTimeout(() => {
              setShouldLoad(false);
              unloadTimerRef.current = null;
            }, 2500);
          }
        });
      },
      { rootMargin: '200px', threshold: 0.15 }
    );
    observer.observe(tileRef.current);
    return () => {
      observer.disconnect();
      if (unloadTimerRef.current) {
        clearTimeout(unloadTimerRef.current);
        unloadTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isActive) return undefined;
    const stop = startNoise(noiseCanvasRef.current);
    return () => stop();
  }, [isActive]);

  return (
    <Card
      ref={tileRef}
      sx={{
        backgroundColor: '#000',
        border: isFullscreen ? 'none' : '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        position: isFullscreen ? 'fixed' : 'relative',
        inset: isFullscreen ? 0 : 'auto',
        width: isFullscreen ? '100vw' : 'auto',
        height: isFullscreen ? '100vh' : 'auto',
        zIndex: isFullscreen ? 2200 : 'auto',
        cursor: 'pointer'
      }}
      onClick={() => onClick(agent)}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: isFullscreen ? '100%' : 'auto',
          paddingTop: isFullscreen ? 0 : '56.25%'
        }}
      >
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onCameraClick(agent);
          }}
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            zIndex: 4,
            color: '#fff',
            backgroundColor: 'rgba(0,0,0,0.45)',
            border: '1px solid rgba(255,255,255,0.2)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.65)' }
          }}
        >
          <VideocamIcon fontSize="small" />
        </IconButton>
        {iframeSrc && (
          <iframe
            title={`viewer-${agent.agent}`}
            src={iframeSrc}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              border: 'none',
              pointerEvents: 'none'
            }}
            allow="autoplay"
          />
        )}
        {isFullscreen && (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onExitFullscreen();
            }}
            sx={{
              position: 'absolute',
              top: 6,
              left: 6,
              zIndex: 5,
              color: '#fff',
              backgroundColor: 'rgba(0,0,0,0.55)',
              border: '1px solid rgba(255,255,255,0.25)',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
        {!isActive && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(circle at center, rgba(40,40,40,0.8), rgba(0,0,0,0.95))',
              overflow: 'hidden'
            }}
          >
            <canvas
              ref={noiseCanvasRef}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                transform: 'translate3d(0,0,0)'
              }}
            />
            <Typography
              variant="subtitle2"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'rgba(255,255,255,0.8)',
                fontWeight: 600
              }}
            >
              {isFullscreen ? 'En pantalla completa' : 'Sin señal'}
            </Typography>
          </Box>
        )}
      </Box>
      {!isFullscreen && (
        <>
          <Divider />
          <Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {agent.agent}{agent.name ? ` - ${agent.name}` : ''}
            </Typography>
            <Chip
              size="small"
              label={statusLabel}
              color="default"
              sx={{ backgroundColor: statusColor, color: statusTextColor }}
            />
          </Box>
        </>
      )}
    </Card>
  );
};

export default function SupervisorMonitoringCenterPage() {
  const api = useApi();
  const { showError } = useNotification();
  const [agents, setAgents] = useState([]);
  const [skills, setSkills] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(true);
  const [tileSize, setTileSize] = useState(320);
  const [pageSize, setPageSize] = useState(9);
  const [page, setPage] = useState(0);
  const [cameraViewerOpen, setCameraViewerOpen] = useState(false);
  const [cameraViewerAgent, setCameraViewerAgent] = useState(null);
  const [fullscreenAgentId, setFullscreenAgentId] = useState(null);
  const [activeScreenStreams, setActiveScreenStreams] = useState(() => new Set());
  const savePrefsTimerRef = useRef(null);

  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const campaignLookup = useMemo(() => {
    const map = new Map();
    campaigns.forEach((campaign) => {
      map.set(String(campaign.idcampaign), campaign.descripcion || `Campaña ${campaign.idcampaign}`);
    });
    return map;
  }, [campaigns]);

  const agentNameLookup = useMemo(() => {
    const map = new Map();
    agents.forEach((agent) => {
      if (agent.agent) {
        map.set(String(agent.agent), agent.name || agent.agent);
      }
    });
    return map;
  }, [agents]);

  const supervisorOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    agents.forEach((agent) => {
      const id = agent.supervisor_id || agent.supervisor;
      const value = id ? String(id).trim() : '';
      if (!value || seen.has(value)) return;
      seen.add(value);
      options.push({ id: value, name: agentNameLookup.get(value) || value });
    });
    return options;
  }, [agents, agentNameLookup]);

  const managerOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    agents.forEach((agent) => {
      const id = agent.manager_id || agent.manager;
      const value = id ? String(id).trim() : '';
      if (!value || seen.has(value)) return;
      seen.add(value);
      options.push({ id: value, name: agentNameLookup.get(value) || value });
    });
    return options;
  }, [agents, agentNameLookup]);

  const loadReferenceData = useCallback(async () => {
    try {
      const [skillsResponse, campaignsResponse] = await Promise.all([
        api.skills.getAll(),
        api.campaigns.getAll()
      ]);
      if (skillsResponse?.success) {
        setSkills(skillsResponse.data?.skills || []);
      }
      if (campaignsResponse?.success) {
        setCampaigns(campaignsResponse.data?.campaigns || []);
      }
    } catch (error) {
      console.error('[MONITOR CENTER] Error cargando catálogos', error);
      showError('No se pudieron cargar las listas de skills/campañas');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAgents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.agents.getStatusBoard();
      if (!response?.success) {
        throw new Error(response?.message || 'Respuesta inválida');
      }
      setAgents(response.data?.agents || []);
    } catch (error) {
      console.error('[MONITOR CENTER] Error cargando agentes', error);
      showError('No se pudo cargar el tablero de supervisión');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadReferenceData();
    loadAgents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(PREFS_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.filters) {
        setFilters({ ...DEFAULT_FILTERS, ...parsed.filters });
      }
      if (typeof parsed?.tileSize === 'number') {
        setTileSize(Math.max(SCREEN_TILE_MIN, Math.min(SCREEN_TILE_MAX, parsed.tileSize)));
      }
      if (typeof parsed?.pageSize === 'number') {
        setPageSize(parsed.pageSize);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (savePrefsTimerRef.current) clearTimeout(savePrefsTimerRef.current);
    savePrefsTimerRef.current = setTimeout(() => {
      try {
        const payload = { filters, tileSize, pageSize };
        window.localStorage.setItem(PREFS_KEY, JSON.stringify(payload));
      } catch {}
    }, 400);
    return () => {
      if (savePrefsTimerRef.current) clearTimeout(savePrefsTimerRef.current);
    };
  }, [filters, tileSize, pageSize]);

  useEffect(() => {
    if (!api?.sse) return undefined;
    const handleStatusEvent = (event) => {
      try {
        const data = JSON.parse(event.data);
        const targetId = data.agentId || data.agent?.agent;
      if (!targetId) return;
        setAgents((prev) => prev.map((agent) => {
          if (agent.agent !== targetId) return agent;
          const nextStatus = data.status || data.agent?.status || agent.status;
          return {
            ...agent,
            status: nextStatus,
            pauseCode: data.pauseCode ?? agent.pauseCode,
            connection: {
              ...(agent.connection || {}),
              connected: data.isConnected ?? agent.connection?.connected ?? false
            }
          };
        }));
      } catch {}
    };
    api.sse.addEventListener('agent-update', handleStatusEvent);
    api.sse.addEventListener('agent-connected', handleStatusEvent);
    api.sse.addEventListener('agent-disconnected', handleStatusEvent);
    api.sse.addEventListener('agent-status', handleStatusEvent);
    return () => {
      api.sse.removeEventListener('agent-update', handleStatusEvent);
      api.sse.removeEventListener('agent-connected', handleStatusEvent);
      api.sse.removeEventListener('agent-disconnected', handleStatusEvent);
      api.sse.removeEventListener('agent-status', handleStatusEvent);
    };
  }, [api?.sse]);

  const filteredAgents = useMemo(() => {
    return agents.filter((agent) => {
      if (filters.agent) {
        const query = filters.agent.trim().toLowerCase();
        const matchesAgent =
          (agent.agent || '').toLowerCase().includes(query) ||
          (agent.name || '').toLowerCase().includes(query) ||
          (agent.extension || '').toLowerCase().includes(query);
        if (!matchesAgent) return false;
      }
      if (filters.campaignId) {
        const matchesCampaign = (agent.campaigns || []).some(
          (id) => String(id) === String(filters.campaignId)
        );
        if (!matchesCampaign) return false;
      }
      if (filters.skillId) {
        const matchesSkill = (agent.skills || []).some(
          (skill) => String(skill.id) === String(filters.skillId)
        );
        if (!matchesSkill) return false;
      }
      if (filters.status) {
        if ((agent.status || '') !== filters.status) return false;
      }
      if (filters.supervisorId) {
        const sup = agent.supervisor_id || agent.supervisor || '';
        if (String(sup).trim() !== filters.supervisorId) return false;
      }
      if (filters.managerId) {
        const mgr = agent.manager_id || agent.manager || '';
        if (String(mgr).trim() !== filters.managerId) return false;
      }
      if (filters.hideDisconnected) {
        if (!agent.connection?.connected) return false;
      }
      if (filters.onlyAgents) {
        if (!isAgentRole(agent)) return false;
      }
      return true;
    });
  }, [agents, filters]);

  useEffect(() => {
    setPage(0);
  }, [filters, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredAgents.length / pageSize));
  const pagedAgents = useMemo(() => {
    const start = page * pageSize;
    return filteredAgents.slice(start, start + pageSize);
  }, [filteredAgents, page, pageSize]);

  const handlePrevPage = useCallback(() => {
    setPage((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setPage((prev) => Math.min(totalPages - 1, prev + 1));
  }, [totalPages]);

  const tileTemplate = useMemo(() => ({
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, ${tileSize}px)`,
    gap: '12px',
    justifyContent: 'flex-start'
  }), [tileSize]);

  const handleTileClick = useCallback((agent) => {
    const agentId = agent?.agent ? String(agent.agent) : null;
    if (!agentId) return;
    setFullscreenAgentId(agentId);
  }, []);

  const closeInlineFullscreen = useCallback(() => {
    setFullscreenAgentId(null);
  }, []);

  const handleCameraClick = useCallback((agent) => {
    setCameraViewerAgent(agent);
    setCameraViewerOpen(true);
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (!event?.data || typeof event.data !== 'object') return;
      if (event.data.type !== 'viewer-stream') return;
      const id = String(event.data.id || '').trim();
      const stream = String(event.data.stream || 'screen').trim();
      if (stream !== 'screen') return;
      if (!id) return;
      setActiveScreenStreams((prev) => {
        const next = new Set(prev);
        if (event.data.status === 'active') {
          next.add(id);
        } else {
          next.delete(id);
        }
        return next;
      });
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <Box sx={{ maxWidth: '100vw', minHeight: '100vh', px: 0, py: 2 }}>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flex: 1 }}>
                <FormControl fullWidth size="small" sx={FILTER_CONTROL_SX}>
                  <InputLabel id="filter-agent-label">Agente / Extensión</InputLabel>
                  <TextField
                    value={filters.agent}
                    onChange={handleFilterChange('agent')}
                    size="small"
                    label="Agente / Extensión"
                  />
                </FormControl>
                <FormControl fullWidth size="small" sx={FILTER_CONTROL_SX}>
                  <InputLabel id="filter-campaign-label">Campaña</InputLabel>
                  <Select
                    labelId="filter-campaign-label"
                    value={filters.campaignId}
                    label="Campaña"
                    onChange={handleFilterChange('campaignId')}
                  >
                    <MenuItem value=""><em>Todas</em></MenuItem>
                    {campaigns.map((campaign) => (
                      <MenuItem key={campaign.idcampaign} value={campaign.idcampaign}>
                        {campaign.descripcion || `Campaña ${campaign.idcampaign}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" sx={FILTER_CONTROL_SX}>
                  <InputLabel id="filter-skill-label">Skill</InputLabel>
                  <Select
                    labelId="filter-skill-label"
                    value={filters.skillId}
                    label="Skill"
                    onChange={handleFilterChange('skillId')}
                  >
                    <MenuItem value=""><em>Todos</em></MenuItem>
                    {skills.map((skill) => (
                      <MenuItem key={skill.id} value={skill.id}>
                        {skill.skillname} ({skill.skilldef})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 200, ...FILTER_CONTROL_SX }}>
                <InputLabel id="filter-status-label">Estado</InputLabel>
                <Select
                  labelId="filter-status-label"
                  value={filters.status}
                  label="Estado"
                  onChange={handleFilterChange('status')}
                >
                  <MenuItem value=""><em>Todos</em></MenuItem>
                  {Array.from(new Set(agents.map((agent) => agent.status).filter(Boolean))).map((status) => (
                    <MenuItem key={status} value={status}>
                      {getAgentStatusLabel(status) || status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 220, ...FILTER_CONTROL_SX }}>
                <InputLabel id="filter-supervisor-label">Supervisor</InputLabel>
                <Select
                  labelId="filter-supervisor-label"
                  value={filters.supervisorId}
                  label="Supervisor"
                  onChange={handleFilterChange('supervisorId')}
                >
                  <MenuItem value=""><em>Todos</em></MenuItem>
                  {supervisorOptions.map((sup) => (
                    <MenuItem key={sup.id} value={sup.id}>{sup.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 220, ...FILTER_CONTROL_SX }}>
                <InputLabel id="filter-manager-label">Gerente</InputLabel>
                <Select
                  labelId="filter-manager-label"
                  value={filters.managerId}
                  label="Gerente"
                  onChange={handleFilterChange('managerId')}
                >
                  <MenuItem value=""><em>Todos</em></MenuItem>
                  {managerOptions.map((mgr) => (
                    <MenuItem key={mgr.id} value={mgr.id}>{mgr.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ minWidth: 220, px: 1 }}>
                <Typography variant="caption" color="text.secondary">Tamaño mosaico</Typography>
                <Slider
                  value={tileSize}
                  min={SCREEN_TILE_MIN}
                  max={SCREEN_TILE_MAX}
                  step={20}
                  onChange={(_, value) => setTileSize(value)}
                  valueLabelDisplay="auto"
                />
              </Box>
              <FormControl size="small" sx={{ minWidth: 140, ...FILTER_CONTROL_SX }}>
                <InputLabel id="page-size-label">Mosaicos</InputLabel>
                <Select
                  labelId="page-size-label"
                  value={pageSize}
                  label="Mosaicos"
                  onChange={(event) => setPageSize(Number(event.target.value))}
                >
                  {[6, 9, 12, 16, 20].map((value) => (
                    <MenuItem key={value} value={value}>{value}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Página {page + 1} de {totalPages} · {filteredAgents.length} agentes
        </Typography>
        <Stack direction="row" spacing={1}>
          <Chip size="small" label="Anterior" onClick={handlePrevPage} disabled={page === 0} />
          <Chip size="small" label="Siguiente" onClick={handleNextPage} disabled={page >= totalPages - 1} />
        </Stack>
      </Stack>

      <Box sx={tileTemplate}>
        {pagedAgents.map((agent) => (
          <ScreenTile
            key={agent.agent}
            agent={agent}
            isActive={activeScreenStreams.has(String(agent.agent))}
            onClick={handleTileClick}
            onCameraClick={handleCameraClick}
            isFullscreen={Boolean(fullscreenAgentId) && String(fullscreenAgentId) === String(agent.agent)}
            onExitFullscreen={closeInlineFullscreen}
          />
        ))}
      </Box>

      <Dialog
        open={cameraViewerOpen}
        onClose={() => { setCameraViewerOpen(false); }}
        fullScreen
        PaperProps={{ sx: { overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ p: 1, backgroundColor: '#000', color: '#fff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2">
              Ver Cámara {cameraViewerAgent?.agent ? `- ${cameraViewerAgent.agent}` : ''}
              {cameraViewerAgent?.name ? ` - ${cameraViewerAgent.name}` : ''}
            </Typography>
            <IconButton onClick={() => { setCameraViewerOpen(false); }} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers={false} sx={{ p: 0, backgroundColor: '#000', overflow: 'hidden' }}>
          {cameraViewerAgent?.agent ? (
            <iframe
              title="Camera Viewer"
              src={`/viewer?id=${encodeURIComponent(cameraViewerAgent.agent)}&type=cam&quality=high&request=1`}
              key={cameraViewerAgent.agent}
              style={{ width: '100%', height: 'calc(100vh - 56px)', border: 'none', display: 'block' }}
              allow="autoplay; fullscreen"
            />
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2">No se pudo identificar el agente.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <style>
        {''}
      </style>
    </Box>
  );
}
